import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ServiceProviderModal from "./ServiceProviderModal";
import ServicesCard from "../../ServicesSection/ServicesCard";
import SubServicesCard from "../../ServicesSection/SubServicesCard";
import BusinessProfile from "./BusinessProfile.js";
import LoadingAnimation from "../../Animations/LoadingAnimation.js";
import {
  cardHover,
  cardOutline,
  fonts,
  headingFonts,
  mainButtonStyles,
  noOutline,
  notFoundBox,
} from "../../../Styles/Styles.js";
import AnalyticsTracker from "../../GASetup/AnalyticsTracker.js";
import { Helmet } from "react-helmet-async";
import ImageCropper from "../Profile/ImageCropper.js";
import { getCroppedImage } from "../Profile/CropImageHelper.js";

export default function MyServices() {
  const [isVerified, setIsVerified] = useState(false);
  const [parentServices, setParentServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [serviceProvider, setServiceProvider] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedImageURL, setCroppedImageURL] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/singleuser`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const userParentServices = [];
        const userSubServices = [];

        data.serviceProvider.skills.forEach((skill) => {
          userParentServices.push({
            _id: skill.parentServiceId._id,
            name: skill.parentServiceId.name,
            description: skill.parentServiceId.description,
            image: skill.parentServiceId.image,
          });

          skill.subServiceIds.forEach((subService) => {
            userSubServices.push({
              _id: subService._id,
              name: subService.name,
              description: subService.description,
              image: subService.image,
              parentServiceId: skill.parentServiceId._id,
            });
          });
        });

        setParentServices(userParentServices);
        setSubServices(userSubServices);
        setServiceProvider(data.serviceProvider);
        setIsVerified(data.serviceProvider.isVerified);
      } else {
        console.error("Failed to fetch services");
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Handle file input change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(URL.createObjectURL(file));
      setIsCropperOpen(true);
    }
  };

  // Handle final cropped image
  const handleCroppedImage = async () => {
    if (!croppedAreaPixels) {
      console.error("Cropped area is not set");
      return;
    }

    try {
      const croppedBlob = await getCroppedImage(
        selectedImage,
        croppedAreaPixels,
        200,
        200
      );
      setCroppedImage(croppedBlob);
      setIsCropperOpen(false);

      // Create a FormData to send image to backend
      const formData = new FormData();
      formData.append("image", croppedBlob, "profile.jpg");

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/serviceProviders/update-profile-image`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (result.imageUrl) {
          setCroppedImageURL(result.imageUrl);
        }
      } else {
        console.error("❌ Failed to update image:", result.message);
      }
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  useEffect(() => {
    if (croppedImage) {
      const objectURL = URL.createObjectURL(croppedImage);
      setCroppedImageURL(objectURL);

      return () => URL.revokeObjectURL(objectURL); // Cleanup memory
    }
  }, [croppedImage]);

  if (loading) {
    return <LoadingAnimation />;
  }

  if (!isVerified) {
    return (
      <Box sx={{ padding: 3 }}>
        <Box sx={{ ...notFoundBox }}>
          <PersonOffOutlinedIcon
            sx={{
              color: "var(--primary-color)",
              fontSize: 50,
              opacity: 0.5,
              marginBottom: 1,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "var(--primary-color)",
              ...headingFonts,
            }}
          >
            Not a Verified Business Owner
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, ...fonts }}>
            You are not currently a verified business owner. To provide
            services, please verify your account as a Business.
          </Typography>
          <Button
            onClick={() => setOpenModal(true)}
            className="main-button-style"
            sx={{
              mt: 2,
              ...mainButtonStyles,
            }}
          >
            <>
              <span>APPLY NOW</span>
              <ArrowForwardOutlinedIcon />
            </>
          </Button>
        </Box>
        {/* Service provider modal */}
        <ServiceProviderModal
          open={openModal}
          onClose={() => setOpenModal(false)}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Business Profile - Listed.ky</title>
        <meta name="description" content="Business Profile - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              ...cardOutline,
              ...cardHover,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                cursor: "pointer",
              }}
            >
              {/* Hidden File Input */}
              <input
                id="profile-image-upload"
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />

              {/* Avatar with Hover Overlay */}
              <label
                htmlFor="profile-image-upload"
                style={{ marginBottom: "0px" }}
              >
                <Avatar
                  src={
                    croppedImageURL ||
                    serviceProvider.image ||
                    "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
                  }
                  alt={serviceProvider?.name?.[0]}
                  sx={{ width: 100, height: 100 }}
                />
              </label>

              {/* Camera Icon Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 100,
                  height: 100,
                  backgroundColor: "rgba(0, 0, 0, 0.5)", // Dark overlay
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0, // Hidden by default
                  transition: "opacity 0.3s ease-in-out",
                  "&:hover": { opacity: 1 }, // Show on hover
                }}
              >
                <label
                  htmlFor="profile-image-upload"
                  style={{ marginBottom: "0px" }}
                >
                  <IconButton
                    component="span"
                    sx={{ color: "white", ...noOutline }}
                  >
                    <CameraAltOutlinedIcon />
                  </IconButton>
                </label>
              </Box>
            </Box>

            {/* <Avatar
              src={
                serviceProvider.image ||
                "https://elasticbeanstalk-us-east-1-908027412287.s3.us-east-1.amazonaws.com/uploads/ProfileImages/default.png"
              }
              alt={serviceProvider.name}
              sx={{
                width: 100,
                height: 100,
                mx: "auto",
                mb: 2,
                fontSize: 32,
                color: "white",
              }}
            /> */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h5" sx={{ ...headingFonts }}>
                  {serviceProvider.name}
                </Typography>
                <BusinessProfile
                  open={openModal}
                  data={serviceProvider}
                  refreshData={fetchServices}
                />
              </Box>

              {serviceProvider.isVerified === "approved" && (
                <Tooltip title="This Service Provider has been verified." arrow>
                  <IconButton
                    size="small"
                    sx={{
                      color: "green",
                      "&:focus, &:active": { outline: "none" },
                    }}
                  >
                    <VerifiedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <Typography variant="body1" gutterBottom sx={{ ...fonts }}>
              @{serviceProvider.username}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              mt: 3,
              ...cardOutline,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
              About
            </Typography>
            <Typography variant="body2" gutterBottom sx={{ ...fonts }}>
              {serviceProvider.description}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box mb={2}>
            {/* Display Parent Services */}
            {parentServices.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
                  Services
                </Typography>
                <Grid container spacing={3}>
                  {parentServices.map((service, index) => (
                    <Grid item xs={12} sm={6} md={6} key={index}>
                      <ServicesCard
                        service={{
                          _id: service._id,
                          name: service.name,
                          description: service.description,
                          image: service.image,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Display Sub-services */}
            {subServices.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom sx={{ ...headingFonts }}>
                  Specialist in:
                </Typography>
                <Grid container spacing={3}>
                  {subServices.map((service, index) => (
                    <Grid item xs={12} sm={6} md={6} key={index}>
                      <SubServicesCard
                        subService={{
                          _id: service._id,
                          name: service.name,
                          description: service.description,
                          image: service.image,
                        }}
                        serviceId={service.parentServiceId}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      {/* Cropping Modal */}
      {isCropperOpen && (
        <ImageCropper
          imageSrc={selectedImage}
          setCroppedAreaPixels={setCroppedAreaPixels} // Pass setter
          onCropComplete={handleCroppedImage}
          onClose={() => setIsCropperOpen(false)}
        />
      )}
    </Box>
  );
}
